'use client';

import type { IDailyReport } from '@repo/sdk';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@repo/ui';
import type { JSX } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

const config: ChartConfig = {
  passed: { label: 'Passed', theme: { light: '#16a34a', dark: '#22c55e' } },
  failed: { label: 'Failed', theme: { light: '#e11d48', dark: '#fb7185' } },
};

export function ActivityChart({
  dailyProgress,
}: {
  dailyProgress: Record<string, IDailyReport>;
}): JSX.Element {
  const data = Object.values(dailyProgress)
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({ date: d.date, passed: d.passed, failed: d.failed }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Activity (28 days)</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center text-sm">No activity yet</div>
        ) : (
          <ChartContainer config={config} className="h-[240px] w-full">
            <AreaChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="fill-passed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-passed)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--color-passed)" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="fill-failed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-failed)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="var(--color-failed)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/30" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={24}
                tickFormatter={(v) =>
                  new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                }
              />
              <YAxis width={28} tickLine={false} axisLine={false} allowDecimals={false} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    }
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="passed"
                stackId="a"
                stroke="var(--color-passed)"
                fill="url(#fill-passed)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="failed"
                stackId="a"
                stroke="var(--color-failed)"
                fill="url(#fill-failed)"
                strokeWidth={2}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
